import LoadingSpinner from '../icons/LoadingSpinner.tsx'
import { useClubAdminListDigestOccurrencesQuery } from '../../features/SpeechAgendaApiSlice.ts'
import { DigestOccurrence } from '../../generated'
import {
  digestOccurrenceStatus,
  DigestOccurrenceTone,
} from './digestOccurrenceStatus.ts'

interface DigestOccurrencesProps {
  clubId: string
}


const toneClassName: Record<DigestOccurrenceTone, string> = {
  success: 'bg-green-50 text-green-700 ring-green-600/20',
  info: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  neutral: 'bg-gray-50 text-gray-600 ring-gray-500/20',
  warning: 'bg-amber-50 text-amber-800 ring-amber-600/20',
}

const openRolesLabel = (count: number): string => {
  if (count === 0) return 'No open roles'
  return count === 1 ? '1 open role' : `${count} open roles`
}

const DigestOccurrenceItem = ({
  occurrence,
}: {
  occurrence: DigestOccurrence
}) => {
  const status = digestOccurrenceStatus(occurrence)
  return (
    <li className="flex flex-col gap-2 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <p className="break-words font-medium text-gray-900">
          {occurrence.meetingName}
        </p>
        <p className="mt-1 text-sm text-gray-600">{occurrence.startDateTime}</p>
        {occurrence.isRegularEvent && (
          <p className="mt-0.5 text-sm text-gray-500">
            {openRolesLabel(occurrence.openRolesCount)}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:flex-shrink-0 sm:flex-col sm:items-end">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${toneClassName[status.tone]}`}>
          {status.label}
        </span>
        {status.reason && (
          <span className="text-xs text-gray-500">{status.reason}</span>
        )}
      </div>
    </li>
  )
}

const DigestOccurrences = ({ clubId }: DigestOccurrencesProps) => {
  const {
    currentData: occurrences,
    isFetching,
    isError,
  } = useClubAdminListDigestOccurrencesQuery(clubId, {
    skip: !clubId,
    // A status changes with the clock and with the hourly task, which no mutation in the browser
    // reports, so the list is read again whenever the admin comes back to it
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  })

  // Not `isLoading`: this stays mounted when the club changes, and then there is no list for the
  // new club yet although the first load is long over. A refetch of the same club keeps the list
  const isPending = isFetching && !occurrences

  return (
    <section className="mt-12 max-w-2xl" aria-labelledby="digest-occurrences">
      <header className="mb-4">
        <h3
          id="digest-occurrences"
          className="text-lg font-semibold text-gray-900">
          Upcoming meetings
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          What the digest does for each meeting in the next 12 weeks.
          Times are in the club&apos;s local time
        </p>
      </header>

      {isPending && (
        <div className="py-6 flex justify-center">
          <LoadingSpinner message="Loading upcoming meetings..." />
        </div>
      )}

      {!isPending && isError && (
        <p className="text-sm text-red-600">
          Unable to load the upcoming meetings right now. Please refresh and try
          again
        </p>
      )}

      {!isError && occurrences && occurrences.length === 0 && (
        <p className="text-sm text-gray-600">
          No meetings in the next 12 weeks
        </p>
      )}

      {!isError && occurrences && occurrences.length > 0 && (
        <ul className="divide-y divide-gray-200 border-t">
          {occurrences.map((occurrence) => (
            <DigestOccurrenceItem
              key={occurrence.occurrenceTimestamp}
              occurrence={occurrence}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

export default DigestOccurrences
